import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminToast from '../admin/AdminToast';
import AcademiaSelect from '../common/AcademiaSelect';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { BIBLIOTHEQUE_CATEGORIES } from '../../utils/elearningBibliothequeCategories';
import ApprenantFormField from './ApprenantFormField';
import ApprenantPageShell from './ApprenantPageShell';
import ApprenantSectionCard from './ApprenantSectionCard';
import ElearningExercisePlayer, {
  ElearningQuizCardShell,
  formatQuizMeta,
} from '../elearning/ElearningExercisePlayer';
import { jsonToExerciseQuestions, initExerciseReponses } from '../elearning/ElearningExerciseEditor';
import ElearningVideoPlayer, {
  ElearningVideoCardShell,
  useElearningMediaUrl,
} from '../elearning/ElearningVideoPlayer';
import ElearningDocumentViewer from '../elearning/ElearningDocumentViewer';
import {
  ElearningForumCardShell,
  ElearningForumCompose,
  ElearningForumMessageList,
  type ForumMessage,
} from '../elearning/ElearningForumThread';
import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import {
  fetchElearningBibliotheque,
  fetchElearningCours,
  fetchElearningForumThread,
  fetchElearningForums,
  fetchElearningHub,
  fetchElearningRessource,
  postElearningForumMessage,
  postElearningProgression,
  submitElearningExercice,
} from '../../services/academiaApprenantService';

type FormationHub = {
  idFiliere: string;
  libelle: string;
  code: string;
  cours: number;
  ressources: number;
  forums: number;
};

type ViewMode = 'hub' | 'formation' | 'ressource' | 'forum';

type ToastState = { message: string; variant: 'success' | 'error' } | null;

const fieldClass =
  'w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

function youtubeEmbedUrl(url?: string | null) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url.includes('embed') ? url : null;
}

function resourceIcon(type: unknown) {
  if (type === 'VIDEO') return 'play_circle';
  if (type === 'EXERCICE') return 'edit_note';
  return 'description';
}

function resourceTypeLabel(type: unknown) {
  if (type === 'VIDEO') return 'Vidéo';
  if (type === 'EXERCICE') return 'Exercice';
  if (type === 'DOCUMENT') return 'Document';
  if (type === 'BIBLIOTHEQUE') return 'Bibliothèque';
  return String(type ?? 'Ressource');
}

function backLinkClass() {
  return 'mb-4 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline';
}

export default function ApprenantElearningPanel() {
  const [view, setView] = useState<ViewMode>('hub');
  const [hub, setHub] = useState<{ formations: FormationHub[]; totals: Record<string, number> } | null>(null);
  const [selectedFiliere, setSelectedFiliere] = useState<FormationHub | null>(null);
  const [tab, setTab] = useState<'cours' | 'bibliotheque' | 'forums'>('cours');
  const [cours, setCours] = useState<Array<Record<string, unknown>>>([]);
  const [bibliotheque, setBibliotheque] = useState<Array<Record<string, unknown>>>([]);
  const [biblioFilters, setBiblioFilters] = useState({ q: '', categorie: '', tag: '' });
  const [forums, setForums] = useState<Array<Record<string, unknown>>>([]);
  const [ressource, setRessource] = useState<Record<string, unknown> | null>(null);
  const [ressourceOriginTab, setRessourceOriginTab] = useState<'cours' | 'bibliotheque'>('cours');
  const [forum, setForum] = useState<Record<string, unknown> | null>(null);
  const [message, setMessage] = useState('');
  const [exerciceReponses, setExerciceReponses] = useState<Record<string, unknown>>({});
  const [toast, setToast] = useState<ToastState>(null);
  const [loading, setLoading] = useState(true);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const toastNode = (
    <AdminToast message={toast?.message ?? null} variant={toast?.variant ?? 'success'} onClose={dismissToast} />
  );
  const ressourceMediaPath =
    view === 'ressource' && ressource ? String(ressource.mediaFilePath ?? '') : null;
  const { url: ressourceFileUrl, error: ressourceMediaError } = useElearningMediaUrl(
    view === 'ressource' && ressource ? String(ressource.id) : '',
    ressourceMediaPath,
  );

  const loadHub = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchElearningHub();
      setHub(data ?? { formations: [], totals: {} });
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Impossible de charger l’e-learning.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadFormation = useCallback(async (f: FormationHub, filters = biblioFilters) => {
    try {
      const [c, b, fo] = await Promise.all([
        fetchElearningCours(f.idFiliere),
        fetchElearningBibliotheque(f.idFiliere, filters),
        fetchElearningForums(f.idFiliere),
      ]);
      setCours(c);
      setBibliotheque(b);
      setForums(fo);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Impossible de charger la formation.', 'error');
    }
  }, [biblioFilters, showToast]);

  useEffect(() => {
    void loadHub();
  }, [loadHub]);

  useRegisterPortalRefresh(loadHub);

  const sideCard = useMemo(
    () => ({
      label: 'Mes formations',
      value: loading ? '…' : String(hub?.formations?.length ?? 0),
      helper: 'Contenus e-learning actifs',
      sideLinks: [
        { label: 'Mes cours', to: '/app/mes-cours', icon: 'list_alt' },
        { label: 'Accueil', to: '/app', icon: 'dashboard' },
      ],
    }),
    [hub?.formations?.length, loading],
  );

  async function openFormation(f: FormationHub) {
    setSelectedFiliere(f);
    setView('formation');
    setTab('cours');
    await loadFormation(f);
  }

  async function openRessource(id: string, origin: 'cours' | 'bibliotheque' = 'cours') {
    try {
      const data = await fetchElearningRessource(id);
      setRessource(data);
      setRessourceOriginTab(origin);
      setView('ressource');
      setExerciceReponses(initExerciseReponses(jsonToExerciseQuestions(data?.contenuJson)));
      await postElearningProgression(id, { statut: 'EN_COURS' });
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Impossible d’ouvrir la ressource.', 'error');
    }
  }

  async function openForum(id: string) {
    try {
      const data = await fetchElearningForumThread(id);
      setForum(data);
      setView('forum');
      setMessage('');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Impossible d’ouvrir le forum.', 'error');
    }
  }

  async function markTermine(id: string) {
    try {
      await postElearningProgression(id, { statut: 'TERMINE' });
      showToast('Progression enregistrée.', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Enregistrement impossible.', 'error');
    }
  }

  async function submitExercice(id: string) {
    try {
      const result = await submitElearningExercice(id, exerciceReponses);
      if (result?.statutCorrection === 'EN_ATTENTE') {
        showToast('Réponses soumises — en attente de correction par votre formateur.', 'success');
      } else {
        showToast(`Score : ${result?.score ?? '—'} %`, 'success');
      }
      const data = await fetchElearningRessource(id);
      setRessource(data);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Soumission impossible.', 'error');
    }
  }

  async function publishForumMessage(forumId: string) {
    try {
      await postElearningForumMessage(forumId, { contenu: message });
      setMessage('');
      setForum(await fetchElearningForumThread(forumId));
      showToast('Message publié.', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Publication impossible.', 'error');
    }
  }

  useEffect(() => {
    if (ressourceMediaError) {
      showToast(ressourceMediaError, 'error');
    }
  }, [ressourceMediaError, showToast]);

  if (view === 'hub') {
    return (
      <ApprenantPageShell
        title="E-learning"
        description="Cours en ligne, vidéos, supports, exercices et forums."
        sideCard={sideCard}
        isLoading={loading}
        onRetry={() => void loadHub()}
      >
        {toastNode}
        {loading ? <p className="text-sm text-on-surface-variant">Chargement…</p> : null}
        <div className="apprenant-elearning-list">
          {(hub?.formations ?? []).map((f) => (
            <article key={f.idFiliere} className="apprenant-mes-cours-card">
              <div className="apprenant-mes-cours-card__accent" aria-hidden />
              <div className="apprenant-mes-cours-card__inner">
                <header className="apprenant-mes-cours-card__head">
                  <span className="apprenant-mes-cours-card__icon">
                    <LucideSymbolIcon name="play_circle" tone="inherit" size={22} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="apprenant-mes-cours-card__title">{f.libelle}</h3>
                    <p className="apprenant-mes-cours-card__code">{f.code}</p>
                  </div>
                  <span className="apprenant-mes-cours-card__badge apprenant-mes-cours-card__badge--info">
                    E-learning
                  </span>
                </header>
                <div className="apprenant-elearning-stat-grid" aria-label="Contenus disponibles">
                  <div className="apprenant-elearning-stat">
                    <span className="apprenant-elearning-stat__value">{f.cours}</span>
                    <span className="apprenant-elearning-stat__label">Cours</span>
                  </div>
                  <div className="apprenant-elearning-stat">
                    <span className="apprenant-elearning-stat__value">{f.ressources}</span>
                    <span className="apprenant-elearning-stat__label">Ressources</span>
                  </div>
                  <div className="apprenant-elearning-stat">
                    <span className="apprenant-elearning-stat__value">{f.forums}</span>
                    <span className="apprenant-elearning-stat__label">Forums</span>
                  </div>
                </div>
                <footer className="apprenant-mes-cours-card__actions">
                  <button
                    type="button"
                    className="apprenant-mes-cours-card__btn apprenant-mes-cours-card__btn--primary w-full justify-center"
                    onClick={() => void openFormation(f)}
                  >
                    <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
                    Accéder au contenu
                  </button>
                </footer>
              </div>
            </article>
          ))}
        </div>
        {(hub?.formations?.length ?? 0) === 0 && !loading ? (
          <div className="apprenant-mes-cours-empty">
            <span className="apprenant-mes-cours-empty__icon">
              <LucideSymbolIcon name="play_circle" tone="primary" size={32} />
            </span>
            <p className="apprenant-mes-cours-empty__text">
              Aucun contenu e-learning pour vos formations actives.
            </p>
            <Link to="/app/formations" className="apprenant-mes-cours-empty__cta">
              Parcourir les formations
            </Link>
          </div>
        ) : null}
      </ApprenantPageShell>
    );
  }

  if (view === 'ressource' && ressource) {
    const hasHostedVideo = ressource.mediaKind === 'video' || (ressource.type === 'VIDEO' && Boolean(ressource.mediaStreamPath));
    const fileUrl = ressourceFileUrl;
    const documentKind =
      ressource.mediaKind === 'pdf' || ressource.mediaKind === 'image'
        ? ressource.mediaKind
        : ressource.mediaFilePath
          ? 'pdf'
          : null;
    const questions = jsonToExerciseQuestions(ressource.contenuJson);
    const derniereReponse = ressource.derniereReponse as {
      statutCorrection?: string;
      score?: number | null;
      feedbackFormateur?: string | null;
    } | null;

    const isBibliotheque = ressource.type === 'BIBLIOTHEQUE';
    const bibliothequeMeta = isBibliotheque
      ? {
          auteurBibliotheque: ressource.auteurBibliotheque ? String(ressource.auteurBibliotheque) : null,
          categorieBibliotheque: ressource.categorieBibliotheque ? String(ressource.categorieBibliotheque) : null,
          langueBibliotheque: ressource.langueBibliotheque ? String(ressource.langueBibliotheque) : null,
          tagsBibliotheque: ressource.tagsBibliotheque,
          anneePublication: ressource.anneePublication as number | string | null | undefined,
          description: ressource.description ? String(ressource.description) : null,
        }
      : ressource.description
        ? { description: String(ressource.description) }
        : null;

    return (
      <ApprenantPageShell
        title={String(ressource.titre)}
        description={isBibliotheque ? 'Bibliothèque numérique' : resourceTypeLabel(ressource.type)}
        sideCard={sideCard}
      >
        <button
          type="button"
          className={backLinkClass()}
          onClick={() => {
            setTab(ressourceOriginTab);
            setView('formation');
          }}
        >
          {ressourceOriginTab === 'bibliotheque' ? '← Retour à la bibliothèque' : '← Retour à la formation'}
        </button>
        {toastNode}
        {hasHostedVideo ? (
          <ElearningVideoPlayer
            ressourceId={String(ressource.id)}
            mediaStreamPath={String(ressource.mediaStreamPath ?? '')}
            videoQualities={Array.isArray(ressource.videoQualities) ? ressource.videoQualities : undefined}
            transcodageStatut={String(ressource.transcodageStatut ?? '')}
            title={String(ressource.titre)}
          />
        ) : null}
        {!hasHostedVideo && ressource.type === 'VIDEO' ? (
          (() => {
            const embed = youtubeEmbedUrl(String(ressource.urlExterne ?? ''));
            return embed ? (
              <ElearningVideoCardShell title={String(ressource.titre)} meta="Vidéo externe">
                <iframe
                  title={String(ressource.titre)}
                  src={embed}
                  className="elearning-video-card__embed"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                />
              </ElearningVideoCardShell>
            ) : (
              <p className="mb-4 text-sm text-on-surface-variant">Vidéo disponible uniquement en lecture intégrée.</p>
            );
          })()
        ) : null}
        {documentKind ? (
          <ElearningDocumentViewer
            fileUrl={fileUrl}
            title={String(ressource.titre)}
            kind={documentKind}
            loading={!fileUrl && Boolean(ressource.mediaFilePath)}
            bibliotheque={bibliothequeMeta}
          />
        ) : null}
        {ressource.type === 'EXERCICE' && questions.length > 0 ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitExercice(String(ressource.id));
            }}
          >
            <ElearningQuizCardShell
              title={String(ressource.titre)}
              meta={formatQuizMeta(questions)}
              notices={[
                ...(derniereReponse?.statutCorrection === 'EN_ATTENTE'
                  ? [
                      {
                        text: 'Vos réponses sont en cours de correction par le formateur.',
                        variant: 'warning' as const,
                      },
                    ]
                  : []),
                ...(derniereReponse?.statutCorrection === 'CORRIGE_MANUEL'
                  ? [
                      {
                        text: `Score final : ${derniereReponse.score ?? '—'} %${derniereReponse.feedbackFormateur ? ` — ${derniereReponse.feedbackFormateur}` : ''}`,
                        variant: 'success' as const,
                      },
                    ]
                  : []),
              ]}
              footer={
                <button
                  type="submit"
                  className="apprenant-mes-cours-card__btn apprenant-mes-cours-card__btn--primary w-full justify-center sm:w-auto"
                >
                  <LucideSymbolIcon name="send" tone="inherit" size={16} />
                  Soumettre le quiz
                </button>
              }
            >
              <ElearningExercisePlayer
                questions={questions}
                value={exerciceReponses}
                onChange={setExerciceReponses}
              />
            </ElearningQuizCardShell>
          </form>
        ) : null}
        {ressource.type !== 'EXERCICE' ? (
          <button
            type="button"
            className="apprenant-mes-cours-card__btn apprenant-mes-cours-card__btn--primary mt-4"
            onClick={() => void markTermine(String(ressource.id))}
          >
            Marquer comme terminé
          </button>
        ) : null}
      </ApprenantPageShell>
    );
  }

  if (view === 'forum' && forum) {
    const messages = (forum.messages ?? []) as ForumMessage[];
    const forumMeta =
      messages.length === 0
        ? 'Aucun message'
        : `${messages.length} message${messages.length > 1 ? 's' : ''}`;

    return (
      <ApprenantPageShell title={String(forum.titre)} description="Forum pédagogique" sideCard={sideCard}>
        {toastNode}
        <button type="button" className={backLinkClass()} onClick={() => setView('formation')}>
          ← Retour à la formation
        </button>
        <ElearningForumCardShell
          title={String(forum.titre)}
          meta={forumMeta}
          description={forum.description ? String(forum.description) : null}
          footer={
            <ElearningForumCompose
              message={message}
              onMessageChange={setMessage}
              fieldClass={fieldClass}
              onSubmit={(e) => {
                e.preventDefault();
                void publishForumMessage(String(forum.id));
              }}
            />
          }
        >
          <ElearningForumMessageList messages={messages} />
        </ElearningForumCardShell>
      </ApprenantPageShell>
    );
  }

  return (
    <ApprenantPageShell
      title={selectedFiliere?.libelle ?? 'E-learning'}
      description={selectedFiliere?.code}
      sideCard={sideCard}
    >
      {toastNode}
      <button type="button" className={backLinkClass()} onClick={() => setView('hub')}>
        ← Mes formations e-learning
      </button>
      <nav className="apprenant-elearning-tabs" aria-label="Sections e-learning">
        {(['cours', 'bibliotheque', 'forums'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`apprenant-elearning-tabs__btn ${tab === t ? 'apprenant-elearning-tabs__btn--active' : ''}`}
          >
            {t === 'cours' ? 'Cours en ligne' : t === 'bibliotheque' ? 'Bibliothèque' : 'Forums'}
          </button>
        ))}
      </nav>

      {tab === 'cours' ? (
        <div className="apprenant-elearning-stack">
          {cours.length === 0 ? (
            <p className="text-sm text-on-surface-variant">Aucun cours en ligne pour cette formation.</p>
          ) : (
            cours.map((c) => (
              <ApprenantSectionCard
                key={String(c.id)}
                icon="menu_book"
                title={String(c.titre)}
                subtitle={c.description ? String(c.description) : undefined}
                flush
              >
                <ul className="apprenant-elearning-resource-list">
                  {((c.ressources ?? []) as Array<Record<string, unknown>>).map((r) => {
                    const done = (r.progression as { statut?: string } | null)?.statut === 'TERMINE';
                    return (
                      <li key={String(r.id)}>
                        <button
                          type="button"
                          className="apprenant-elearning-resource"
                          onClick={() => void openRessource(String(r.id))}
                        >
                          <span className="apprenant-elearning-resource__icon">
                            <LucideSymbolIcon name={resourceIcon(r.type)} tone="inherit" size={18} />
                          </span>
                          <span className="apprenant-elearning-resource__body">
                            <span className="apprenant-elearning-resource__title">{String(r.titre)}</span>
                            <span className="apprenant-elearning-resource__meta">{resourceTypeLabel(r.type)}</span>
                          </span>
                          {done ? <span className="apprenant-elearning-resource__badge">Terminé</span> : null}
                          <span className="apprenant-elearning-resource__chevron" aria-hidden>
                            <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </ApprenantSectionCard>
            ))
          )}
        </div>
      ) : null}

      {tab === 'bibliotheque' ? (
        <>
          <div className="apprenant-elearning-filters">
            <input
              className={fieldClass}
              placeholder="Rechercher…"
              value={biblioFilters.q}
              onChange={(e) => setBiblioFilters({ ...biblioFilters, q: e.target.value })}
            />
            <ApprenantFormField label="Catégorie" htmlFor="biblio-categorie">
              <AcademiaSelect
                id="biblio-categorie"
                value={biblioFilters.categorie}
                onChange={(e) => setBiblioFilters({ ...biblioFilters, categorie: e.target.value })}
              >
                <option value="">Toutes les catégories</option>
                {BIBLIOTHEQUE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </AcademiaSelect>
            </ApprenantFormField>
            <input
              className={fieldClass}
              placeholder="Tag"
              value={biblioFilters.tag}
              onChange={(e) => setBiblioFilters({ ...biblioFilters, tag: e.target.value })}
            />
            <button
              type="button"
              className="apprenant-mes-cours-card__btn apprenant-mes-cours-card__btn--primary justify-center"
              onClick={() => selectedFiliere && void loadFormation(selectedFiliere, biblioFilters)}
            >
              Filtrer
            </button>
          </div>
          <div className="apprenant-elearning-media-grid">
            {bibliotheque.map((r) => (
              <button
                key={String(r.id)}
                type="button"
                className="apprenant-elearning-media-card"
                onClick={() => void openRessource(String(r.id), 'bibliotheque')}
              >
                <span className="apprenant-elearning-media-card__icon">
                  <LucideSymbolIcon name="library_big" tone="inherit" size={20} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="apprenant-elearning-media-card__title">{String(r.titre)}</span>
                  <span className="apprenant-elearning-media-card__meta">
                    {[r.auteurBibliotheque, r.categorieBibliotheque, r.langueBibliotheque]
                      .filter(Boolean)
                      .map(String)
                      .join(' · ') || 'Ressource documentaire'}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {bibliotheque.length === 0 ? (
            <p className="mt-3 text-sm text-on-surface-variant">Aucune ressource ne correspond à votre recherche.</p>
          ) : null}
        </>
      ) : null}

      {tab === 'forums' ? (
        <div className="apprenant-elearning-stack">
          {forums.length === 0 ? (
            <p className="text-sm text-on-surface-variant">Aucun forum pour cette formation.</p>
          ) : (
            forums.map((f) => {
              const count = Number(f.messagesCount ?? 0);
              return (
                <button
                  key={String(f.id)}
                  type="button"
                  className="apprenant-elearning-forum-card"
                  onClick={() => void openForum(String(f.id))}
                >
                  <span className="apprenant-elearning-forum-card__icon" aria-hidden>
                    <LucideSymbolIcon name="forum" tone="inherit" size={20} />
                  </span>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="apprenant-elearning-forum-card__title">{String(f.titre)}</span>
                    {f.description ? (
                      <span className="apprenant-elearning-forum-card__description">{String(f.description)}</span>
                    ) : (
                      <span className="apprenant-elearning-forum-card__description">
                        {f.dateCreation
                          ? `Créé le ${new Date(String(f.dateCreation)).toLocaleDateString('fr-FR')}`
                          : 'Discussion pédagogique'}
                      </span>
                    )}
                  </span>
                  <span className="apprenant-elearning-forum-card__count">
                    {count} message{count !== 1 ? 's' : ''}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </ApprenantPageShell>
  );
}
